import React, { useState, useEffect, useRef, useMemo } from "react";
import "./HackerNewsAirspace.css";

const HN_API_BASE = "https://hacker-news.firebaseio.com/v0";
const TOP_LIMIT = 30;
const NEW_LIMIT = 30;
const TRAIL_MAX_AGE = 10.2;         // seconds
const MAX_TRAIL_POINTS = 18;
const OUT_OF_BOUNDS_MARGIN = 120;

function randRange(min, max) {
  return Math.random() * (max - min) + min;
}

function extractKeyword(title) {
  if (!title || typeof title !== "string") return "misc";

  const stopwords = new Set([
    "the","a","an","and","or","for","with","from","about","into","over","after",
    "to","of","in","on","at","by","is","are","was","were","be","this","that",
    "these","those","how","what","why","when","which","using","use","your",
    "my","our","their","you","we","they","it","as","if","not","just","new",
    "into","out","up","down","vs","vs.","can","could","should","will","would"
  ]);

  const cleaned = title
    .replace(/["“”'’]/g, "")
    .replace(/[^a-zA-Z0-9+]+/g, " ")
    .trim()
    .toLowerCase();

  if (!cleaned) return "misc";

  const tokens = cleaned.split(/\s+/).filter(Boolean);

  let best = null;

  for (const t of tokens) {
    if (stopwords.has(t)) continue;
    if (t.length < 4 && !/^[a-z]*js$|^go$|^c\+\+$|^ai$|^ui$|^ux$/.test(t)) continue;
    best = t;
    break;
  }

  if (!best) best = tokens[0] || "misc";

  best = best
    .replace(/^(javascript)$/, "js")
    .replace(/^(typescript)$/, "ts")
    .replace(/^(machinelearning|mlops)$/, "ml");

  return best;
}

function getSiteType(story) {
  if (!story) return "DISC";
  const url = (story.url || "").toLowerCase();
  if (!url) return "DISC";

  if (url.includes("github.com") || url.includes("gitlab.") || url.includes("bitbucket.org")) {
    return "<>";
  }

  if (
    url.endsWith(".pdf") ||
    url.includes("arxiv.org") ||
    url.includes("docs.") ||
    url.includes("readthedocs") ||
    url.includes("/documentation")
  ) {
    return "DOC";
  }

  if (
    url.includes("medium.com") ||
    url.includes("substack.com") ||
    url.includes("dev.to") ||
    url.includes("blog.")
  ) {
    return "BLOG";
  }

  return "BLOG";
}

async function fetchStory(id) {
  const res = await fetch(`${HN_API_BASE}/item/${id}.json`);
  if (!res.ok) throw new Error("Failed to fetch story " + id);
  return res.json();
}

async function fetchStories(kind, limit) {
  const listRes = await fetch(`${HN_API_BASE}/${kind}.json`);
  if (!listRes.ok) throw new Error("Failed to fetch " + kind);
  const ids = (await listRes.json()).slice(0, limit);
  const storyPromises = ids.map((id) => fetchStory(id).catch(() => null));
  const stories = await Promise.all(storyPromises);
  return stories.filter(Boolean);
}

function createPlane(story, kind, viewport, nextId) {
  const { width, height } = viewport;
  const speed = kind === "top" ? randRange(40, 65) : randRange(30, 55); // slower

  const edge = Math.floor(Math.random() * 4);
  let x, y, angle;

  if (edge === 0) {
    x = -OUT_OF_BOUNDS_MARGIN / 2;
    y = Math.random() * height;
    angle = randRange(-Math.PI / 4, Math.PI / 4);
  } else if (edge === 1) {
    x = width + OUT_OF_BOUNDS_MARGIN / 2;
    y = Math.random() * height;
    angle = randRange((3 * Math.PI) / 4, (5 * Math.PI) / 4);
  } else if (edge === 2) {
    x = Math.random() * width;
    y = -OUT_OF_BOUNDS_MARGIN / 2;
    angle = randRange(Math.PI / 2 - Math.PI / 4, Math.PI / 2 + Math.PI / 4);
  } else {
    x = Math.random() * width;
    y = height + OUT_OF_BOUNDS_MARGIN / 2;
    angle = randRange(-Math.PI / 2 - Math.PI / 4, -Math.PI / 2 + Math.PI / 4);
  }

  const vx = Math.cos(angle) * speed;
  const vy = Math.sin(angle) * speed;

  return {
    id: nextId,
    story,
    kind,
    x,
    y,
    vx,
    vy,
    angleDeg: (angle * 180) / Math.PI,
    trail: [],
    keyword: extractKeyword(story.title),
    siteType: getSiteType(story),
  };
}

function HackerNewsAirspace() {
  const [topStories, setTopStories] = useState([]);
  const [newStories, setNewStories] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const [error, setError] = useState(null);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [clickedStories, setClickedStories] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedStoryId, setSelectedStoryId] = useState(null);
  const [hoveredPlaneId, setHoveredPlaneId] = useState(null);
  const [isQueueOpenMobile, setIsQueueOpenMobile] = useState(false);

  const planeIdRef = useRef(1);
  const seenNewRef = useRef(new Set());
  const topIdsRef = useRef(new Set());
  const viewportWidth = viewport.width;
  const viewportHeight = viewport.height;

  // viewport
  useEffect(() => {
    const updateViewport = () => {
      setViewport({
        width:
          window.innerWidth || document.documentElement.clientWidth || 1024,
        height:
          window.innerHeight || document.documentElement.clientHeight || 768,
      });
    };
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  // fetch HN
  useEffect(() => {
    let cancelled = false;

    const loadAll = async () => {
      try {
        setError(null);
        const [top, fresh] = await Promise.all([
          fetchStories("topstories", TOP_LIMIT),
          fetchStories("newstories", NEW_LIMIT),
        ]);
        if (cancelled) return;
        setTopStories(top);
        setNewStories(fresh);
        setInitialLoaded(true);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError("Network problem while talking to Hacker News.");
        }
      }
    };

    loadAll();

    const intervalId = setInterval(async () => {
      try {
        const [top, fresh] = await Promise.all([
          fetchStories("topstories", TOP_LIMIT),
          fetchStories("newstories", NEW_LIMIT),
        ]);
        if (cancelled) return;
        setTopStories(top);
        setNewStories(fresh);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError("Intermittent HN API error. Display is still running.");
        }
      }
    }, 60000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  // keep topIds in ref
  useEffect(() => {
    topIdsRef.current = new Set(topStories.map((s) => s.id));
  }, [topStories]);

  // spawn planes for top + fresh new (<= 30 min, not in top)
  useEffect(() => {
    if (!viewportWidth || !viewportHeight) return;
    const spawnViewport = { width: viewportWidth, height: viewportHeight };

    const nowSec = Date.now() / 1000;
    const currentTopIds = new Set(topStories.map((s) => s.id));

    const freshNewStories = newStories.filter((story) => {
      if (!story || typeof story.time !== "number") return false;
      const ageSec = nowSec - story.time;
      return ageSec <= 20 * 60 && !currentTopIds.has(story.id);
    });

    setPlanes((prevPlanes) => {
      const nextPlanes = [...prevPlanes];
      const existingByStoryId = new Map();

      for (const p of nextPlanes) {
        if (p && p.story) {
          existingByStoryId.set(p.story.id, p);
        }
      }

      const isHighLoad = nextPlanes.length >= 45;

      // top stories
      topStories.forEach((story) => {
        if (!story) return;
        const existing = existingByStoryId.get(story.id);
        if (existing) {
          if (existing.kind !== "top") {
            existing.kind = "top";
          }
        } else {
          const plane = createPlane(
            story,
            "top",
            spawnViewport,
            planeIdRef.current++
          );
          nextPlanes.push(plane);
          existingByStoryId.set(story.id, plane);
        }
      });

      // fresh new stories
      freshNewStories.forEach((story) => {
        if (!story) return;

        if (!seenNewRef.current.has(story.id)) {
          seenNewRef.current.add(story.id);
        }

        const existing = existingByStoryId.get(story.id);
        if (!existing && !isHighLoad) {
          const plane = createPlane(
            story,
            "new",
            spawnViewport,
            planeIdRef.current++
          );
          nextPlanes.push(plane);
          existingByStoryId.set(story.id, plane);
        }
      });

      return nextPlanes;
    });
  }, [topStories, newStories, viewportWidth, viewportHeight]);

  // animation loop
  useEffect(() => {
    if (!viewportWidth || !viewportHeight) return;
    const frameViewport = { width: viewportWidth, height: viewportHeight };

    let animationFrameId;
    let lastTime = performance.now();

    const animate = (time) => {
      const dt = (time - lastTime) / 1000;
      lastTime = time;

      if (dt <= 0 || dt > 1) {
        animationFrameId = requestAnimationFrame(animate);
        return;
      }

      if (isPaused) {
        animationFrameId = requestAnimationFrame(animate);
        return;
      }

      setPlanes((prevPlanes) => {
        if (!prevPlanes.length) return prevPlanes;

        const updated = [];
        const topIds = topIdsRef.current;
        const nowSec = Date.now() / 1000;

        for (const plane of prevPlanes) {
          if (!plane || !plane.story) continue;

          let x = plane.x + plane.vx * dt;
          let y = plane.y + plane.vy * dt;

          const outOfBounds =
            x < -OUT_OF_BOUNDS_MARGIN ||
            x > viewportWidth + OUT_OF_BOUNDS_MARGIN ||
            y < -OUT_OF_BOUNDS_MARGIN ||
            y > viewportHeight + OUT_OF_BOUNDS_MARGIN;

          const newTrail = (plane.trail || []).concat([{ x, y, age: 0 }]);
          const trimmedTrail = newTrail
            .map((p) => ({ ...p, age: p.age + dt }))
            .filter((p) => p.age < TRAIL_MAX_AGE)
            .slice(-MAX_TRAIL_POINTS);

          if (outOfBounds) {
            const story = plane.story;

            if (plane.kind === "top" && topIds && topIds.has(story.id)) {
              const respawned = createPlane(
                story,
                "top",
                frameViewport,
                planeIdRef.current++
              );
              updated.push(respawned);
            } else if (plane.kind === "new") {
              const isFresh =
                typeof story.time === "number" &&
                nowSec - story.time <= 20 * 60;

              const isNowTop = topIds && topIds.has(story.id);

              if (isFresh) {
                const kind = isNowTop ? "top" : "new";
                const respawned = createPlane(
                  story,
                  kind,
                  frameViewport,
                  planeIdRef.current++
                );
                updated.push(respawned);
              } else if (isNowTop) {
                const respawned = createPlane(
                  story,
                  "top",
                  frameViewport,
                  planeIdRef.current++
                );
                updated.push(respawned);
              }
            }

            continue;
          }

          updated.push({
            ...plane,
            x,
            y,
            trail: trimmedTrail,
          });
        }

        return updated;
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [viewportWidth, viewportHeight, isPaused]);

  const stats = useMemo(() => {
    const nowSec = Date.now() / 1000;
    const topCount = topStories.length;
    const freshNewCount = newStories.filter((s) => {
      if (!s || typeof s.time !== "number") return false;
      return nowSec - s.time <= 20 * 60;
    }).length;

    let scoreSum = 0;
    let maxScore = 0;
    let ageSumMin = 0;

    topStories.forEach((s) => {
      const score = s.score || 0;
      scoreSum += score;
      if (score > maxScore) maxScore = score;

      if (s.time) {
        ageSumMin += (nowSec - s.time) / 60;
      }
    });

    const avgScore = topCount ? (scoreSum / topCount).toFixed(1) : "–";
    const avgAgeMinutes = topCount ? (ageSumMin / topCount).toFixed(1) : "–";

    const activePlanes = planes.length;
    let speedSum = 0;
    planes.forEach((p) => {
      const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
      speedSum += speed;
    });
    const avgSpeed = activePlanes ? (speedSum / activePlanes).toFixed(1) : "–";

    let airspaceLoad = "low";
    if (activePlanes >= 45) {
      airspaceLoad = "high";
    } else if (activePlanes >= 25) {
      airspaceLoad = "medium";
    }

    return {
      topCount,
      newCount: freshNewCount,
      avgScore,
      maxScore,
      avgAgeMinutes,
      activePlanes,
      avgSpeed,
      totalNewSeen: seenNewRef.current.size,
      storiesVisited: clickedStories.length,
      airspaceLoad,
    };
  }, [topStories, newStories, planes, clickedStories]);

  const handlePlaneClick = (plane) => {
    const story = plane.story;
    if (!story) return;

    setSelectedStoryId(story.id);

    setClickedStories((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.story.id === story.id
      );
      let next = [...prev];
      if (existingIndex !== -1) {
        next.splice(existingIndex, 1);
      }
      next.unshift({
        story,
        kind: plane.kind,
        clickedAt: Date.now(),
      });
      if (next.length > 8) {
        next = next.slice(0, 8);
      }
      return next;
    });

    const url = story.url || `https://news.ycombinator.com/item?id=${story.id}`;

    try {
      window.open(url, "_blank", "noopener");
    } catch {
      window.location.href = url;
    }
  };

  const hoveredPlane = useMemo(() => {
    if (!isPaused || !hoveredPlaneId) return null;
    return planes.find((p) => p.id === hoveredPlaneId) || null;
  }, [isPaused, hoveredPlaneId, planes]);

  const isMobile = viewport.width && viewport.width <= 720;

  return (
    <div className="hn-root">
      <div className="sky-layer">
        <div className="starfield" />
      </div>

      <div className="planes-layer">
        {planes.map((plane) => {
          const isSelected =
            plane.story && plane.story.id === selectedStoryId;
          return (
            <React.Fragment key={plane.id}>
              {plane.trail &&
                plane.trail.map((point, idx) => {
                  const opacity = Math.max(
                    0,
                    1 - point.age / TRAIL_MAX_AGE
                  );
                  const size = 3 + (1 - opacity) * 2;
                  return (
                    <div
                      key={plane.id + "-trail-" + idx}
                      className={
                        "plane-trail-dot " +
                        (plane.kind === "top" ? "top" : "new")
                      }
                      style={{
                        transform: `translate3d(${point.x}px, ${point.y}px, 0)`,
                        opacity,
                        width: size + "px",
                        height: size + "px",
                      }}
                    />
                  );
                })}
              <div
                className={
                  "plane " +
                  (plane.kind === "top" ? "plane-top" : "plane-new") +
                  (isSelected ? " plane-selected" : "")
                }
                style={{
                  transform: `translate3d(${plane.x}px, ${plane.y}px, 0) rotate(${plane.angleDeg}deg)`,
                }}
                onClick={() => handlePlaneClick(plane)}
                onMouseEnter={() => {
                  if (isPaused) {
                    setHoveredPlaneId(plane.id);
                  }
                }}
                onMouseLeave={() => {
                  setHoveredPlaneId((prev) =>
                    prev === plane.id ? null : prev
                  );
                }}
              >
                {isSelected && <div className="plane-selection-ring" />}
                <div className="plane-hitbox" />
                <div className="plane-keyword-badge">
                  <span>{plane.keyword}</span>
                </div>
                <div className="plane-body" />
                <div className="plane-site-icon">{plane.siteType}</div>
              </div>
            </React.Fragment>
          );
        })}

        {hoveredPlane && hoveredPlane.story && (
          <div
            className="plane-popover"
            style={{
              transform: `translate3d(${hoveredPlane.x + 24}px, ${
                hoveredPlane.y - 10
              }px, 0)`,
            }}
          >
            <div className="plane-popover-title">
              {hoveredPlane.story.title || "Untitled story"}
            </div>
            <div className="plane-popover-meta">
              <span>
                Score:{" "}
                {typeof hoveredPlane.story.score === "number"
                  ? hoveredPlane.story.score
                  : "–"}
              </span>
              <span>
                Comments:{" "}
                {typeof hoveredPlane.story.descendants === "number"
                  ? hoveredPlane.story.descendants
                  : "–"}
              </span>
              <span>
                Type: {hoveredPlane.kind === "top" ? "Top 30" : "New"}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="app-overlay">
        <div className="app-title">
          <div className="app-title-main">Hacker News Airspace</div>
          <div className="app-title-sub">
            Live front page as paper aircraft
          </div>
          <div className="legend">
            <div className="legend-item">
              <span className="legend-dot top" />
              <span>Top 30</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot new" />
              <span>New arrivals (&lt; 20 min)</span>
            </div>
          </div>
        </div>

        <aside className="stats-panel">
          <div className="stats-header">
            <div className="stats-title">Flight Telemetry</div>
            <div className="stats-header-right">
              <button
                type="button"
                className={"pause-button" + (isPaused ? " paused" : "")}
                onClick={() => setIsPaused((prev) => !prev)}
              >
                {isPaused ? "Play" : "Pause"}
              </button>
            </div>
          </div>

          <div className="stats-grid">
            <div>
              <div className="stats-item-label">Top Stories</div>
              <div className="stats-item-value">{stats.topCount}</div>
            </div>
            <div>
              <div className="stats-item-label">Fresh New (&lt;20m)</div>
              <div className="stats-item-value">{stats.newCount}</div>
            </div>
            <div>
              <div className="stats-item-label">Avg. Score (Top)</div>
              <div className="stats-item-value">{stats.avgScore}</div>
            </div>
            <div>
              <div className="stats-item-label">Highest Score</div>
              <div className="stats-item-value">
                {stats.maxScore || "–"}
              </div>
            </div>
            <div>
              <div className="stats-item-label">Avg. Story Age</div>
              <div className="stats-item-value muted">
                {stats.avgAgeMinutes === "–"
                  ? "–"
                  : stats.avgAgeMinutes + " min"}
              </div>
            </div>
            <div>
              <div className="stats-item-label">Active Airplanes</div>
              <div className="stats-item-value">
                {stats.activePlanes}
              </div>
            </div>
            <div>
              <div className="stats-item-label">Stories Visited</div>
              <div className="stats-item-value">
                {stats.storiesVisited}
              </div>
            </div>
            <div>
              <div className="stats-item-label">Airspace Load</div>
              <div className="stats-item-value muted">
                {stats.airspaceLoad.toUpperCase()}
              </div>
            </div>
          </div>

          <div className="stats-footer">
            <div className="hint-text">
              Fetches live from the official Hacker News API every 60
              seconds. Animation can be paused; data keeps updating.
            </div>
          </div>
        </aside>

        {clickedStories.length > 0 && (
          <aside className="queue-sidebar">
            <div className="queue-header-row">
              <div className="queue-title">Recently Visited</div>
              {isMobile && (
                <button
                  type="button"
                  className="queue-toggle-btn"
                  onClick={() =>
                    setIsQueueOpenMobile((open) => !open)
                  }
                >
                  {isQueueOpenMobile ? "Hide" : "Show"}
                </button>
              )}
            </div>
            {(!isMobile || isQueueOpenMobile) && (
              <div className="queue-list">
                {clickedStories.map((entry, idx) => {
                  const story = entry.story;
                  const siteType = getSiteType(story);
                  const isTop = entry.kind === "top";
                  const url =
                    story.url ||
                    `https://news.ycombinator.com/item?id=${story.id}`;
                  return (
                    <div
                      className="queue-item"
                      key={story.id + "-" + idx}
                    >
                      <div className="queue-item-header">
                        <a
                          className="queue-item-title"
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {story.title || "Untitled story"}
                        </a>
                      </div>
                      <div className="queue-item-meta">
                        <span
                          className={
                            "queue-item-kind-pill " +
                            (isTop
                              ? "queue-item-kind-top"
                              : "queue-item-kind-new")
                          }
                        >
                          {isTop ? "TOP" : "NEW"}
                        </span>
                        <span className="queue-item-type-pill">
                          {siteType}
                        </span>
                        {typeof story.score === "number" && (
                          <span>Score {story.score}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </aside>
        )}

        <div className="made-by">
          made by{" "}
          <a
            href="https://ecaterina.app"
            target="_blank"
            rel="noopener noreferrer"
          >
            ecaterina.app
          </a>
        </div>

        {!initialLoaded && !error && (
          <div className="loading-banner">
            <div className="loading-spinner" />
            <div>Connecting to Hacker News live feed...</div>
          </div>
        )}

        {error && (
          <div className="error-banner">
            <div className="error-dot" />
            <div>{error}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HackerNewsAirspace;
